import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Crew, MovingCrew, Stop } from "@/types";
import {
  cn,
  formatDateWithOrdinal,
  formatMoveKind,
  formatStartWindow,
} from "@/lib/utils";
import { parseISO } from "date-fns";
import {
  useGetCrewAvailabilityQuery,
  useGetMoveQuery,
  useUpdateCrewMutation,
} from "@/store/moves/apis";
import { MessageSquareDot } from "lucide-react";
import { CREW_ROLES, SERVICE_TYPES } from "@/constants";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useSelector } from "react-redux";
import { getSelectedMover } from "@/store/mover/slice";
import { Loading } from "@/components/Loading";
import {
  faPaperPlane,
  faUserHair,
} from "@awesome.me/kit-9096fb77ac/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface Props {
  move_id?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface MovingCrewWithDestroy extends MovingCrew {
  destroy?: boolean;
}

export const AssignCrew = ({ move_id, open, onOpenChange }: Props) => {
  const mover = useSelector(getSelectedMover);
  const { data: move } = useGetMoveQuery(move_id, { skip: !move_id });
  const [movingCrew, setMovingCrew] = useState<MovingCrewWithDestroy[]>([]);
  const [assignedCrew, setAssignedCrew] = useState<Crew[]>([]);
  const [availableCrew, setAvailableCrew] = useState<Crew[]>([]);
  const [notAvailableCrew, setNotAvailableCrew] = useState<Crew[]>([]);
  const [wasUnavailable, setWasUnavailable] = useState<Crew[]>([]);
  const { data, isLoading: isLoadingCrewAvailability } =
    useGetCrewAvailabilityQuery(move?.id, {
      skip: !move?.id,
      refetchOnMountOrArgChange: true,
    });
  const [updateCrew, { isLoading: isUpdating }] = useUpdateCrewMutation();
  const [allCrew, setAllCrew] = useState<Crew[]>([]);
  const [ts, setTs] = useState(0);

  useEffect(() => {
    if (move && data) {
      setMovingCrew(move.moving_crews);
      setAssignedCrew(move.assigned_crew);
      setAvailableCrew(data.available);
      setNotAvailableCrew(data.not_available);
      setAllCrew([
        ...move.assigned_crew,
        ...data.available,
        ...data.not_available,
      ]);
    }
  }, [move, data]);

  function addToCrew(crew: Crew, role: keyof typeof CREW_ROLES) {
    setMovingCrew([
      ...movingCrew,
      { user_id: crew.id, move_id: move.id, role },
    ]);

    const assigned = [...assignedCrew, crew];
    const available = availableCrew.filter((c) => c.id !== crew.id);
    const notAvailable = notAvailableCrew.filter((c) => c.id !== crew.id);
    if (isNotAvailableCrew(crew)) {
      setWasUnavailable([...wasUnavailable, crew]);
    }

    setAssignedCrew(assigned);
    setAvailableCrew(available);
    setNotAvailableCrew(notAvailable);
    setAllCrew([...assigned, ...available, ...notAvailable]);
  }

  function removeFromCrew(crew?: Crew) {
    if (!crew) return;

    const newMovingCrew = movingCrew.reduce(
      (acc: MovingCrewWithDestroy[], c) => {
        if (c.user_id === crew.id) {
          // if it has an id, mark for destruction, otherwise exclude it
          return c.id ? [...acc, { ...c, destroy: true }] : acc;
        }
        return [...acc, c];
      },
      []
    );
    setMovingCrew(newMovingCrew);

    const assigned = assignedCrew.filter((c) => c.id !== crew.id);
    let available = availableCrew;
    let notAvailable = notAvailableCrew;
    if (wasUnavailable.find((c) => c.id === crew.id)) {
      notAvailable = [...notAvailable, crew];
    } else {
      available = [...available, crew];
    }

    setAssignedCrew(assigned);
    setAvailableCrew(available);
    setNotAvailableCrew(notAvailable);
    setAllCrew([...assigned, ...available, ...notAvailable]);
  }

  function changeRole(mc: MovingCrew, role: keyof typeof CREW_ROLES) {
    const newMovingCrew = movingCrew.map((c) =>
      c.user_id === mc.user_id ? { ...c, role } : c
    );
    setMovingCrew(newMovingCrew);

    setAllCrew([...assignedCrew, ...availableCrew, ...notAvailableCrew]);
    setTs(ts + 1);
  }

  function crewMember(id: number) {
    return mover?.crew.find((c) => c.id === id);
  }

  async function save() {
    const payload = {
      moving_crews_attributes: movingCrew.map((c) => ({
        id: c.id,
        user_id: c.user_id,
        role: c.role,
        _destroy: c.destroy,
      })),
    };

    try {
      await updateCrew({ id: move.id, crew: payload });
      onOpenChange(false);
    } catch (error) {
      console.error(error);
    }
  }

  function isCrewMember(crew: Crew) {
    return assignedCrew.find((mc) => mc.id === crew.id);
  }

  function isAvailableCrew(crew: Crew) {
    return availableCrew.find((c) => c.id === crew.id);
  }

  function isNotAvailableCrew(crew: Crew) {
    return notAvailableCrew.find((c) => c.id === crew.id);
  }

  function renderCrewMemberActions(crew: Crew) {
    const mc = movingCrew.find((mc) => mc.user_id === crew.id);
    if (!mc) return null;

    if (mc.role === "leader") {
      return (
        <>
          {/* <div className="col-span-2">Leader</div> */}
          <div className="col-span-2">
            <Button
              variant="secondary"
              size="sm"
              className="rounded-full"
              onClick={() => changeRole(mc, "helper")}
            >
              Assign Helper
            </Button>
          </div>
          <div className="col-span-2">
            <Button
              variant="destructive"
              size="sm"
              className="rounded-full"
              onClick={() => removeFromCrew(crewMember(mc.user_id))}
            >
              Remove from Crew
            </Button>
          </div>
        </>
      );
    } else if (mc.role === "helper") {
      return (
        <>
          {/* <div className="col-span-2">Helper</div> */}
          <div className="col-span-2">
            <Button
              variant="secondary"
              size="sm"
              className="rounded-full"
              onClick={() => changeRole(mc, "leader")}
            >
              Assign Lead
            </Button>
          </div>
          <div className="col-span-2">
            <Button
              variant="destructive"
              size="sm"
              className="rounded-full"
              onClick={() => removeFromCrew(crewMember(mc.user_id))}
            >
              Remove from Crew
            </Button>
          </div>
        </>
      );
    }
  }

  function renderAvailableCrewActions(crew: Crew) {
    return (
      <>
        {/* <div className="col-span-2">Available</div> */}
        <div className="col-span-2">
          <Button
            variant="secondary"
            size="sm"
            className="rounded-full"
            onClick={() => addToCrew(crew, "leader")}
          >
            Assign Lead
          </Button>
        </div>
        <div className="col-span-2">
          <Button
            variant="secondary"
            size="sm"
            className="rounded-full"
            onClick={() => addToCrew(crew, "helper")}
          >
            Assign Helper
          </Button>
        </div>
      </>
    );
  }

  function renderNotAvailableCrewActions(crew: Crew) {
    return (
      <>
        <div className="col-span-2 text-red-500 font-semibold">
          Not Available
        </div>
        <div className="col-span-2">
          <Button
            variant="secondary"
            size="sm"
            className="rounded-full"
            onClick={() => {
              if (
                window.confirm(
                  `Are you sure you want to override and assign ${crew.first_name} ${crew.last_name} to this job?`
                )
              ) {
                addToCrew(crew, "leader");
              }
            }}
          >
            Assign Lead
          </Button>
        </div>
        <div className="col-span-2">
          <Button
            variant="secondary"
            size="sm"
            className="rounded-full"
            onClick={() => {
              if (
                window.confirm(
                  `Are you sure you want to override and assign ${crew.first_name} ${crew.last_name} to this job?`
                )
              ) {
                addToCrew(crew, "helper");
              }
            }}
          >
            Assign Helper
          </Button>
        </div>
      </>
    );
  }

  if (!move) return null;
  if (isLoadingCrewAvailability) return <Loading />;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className={cn(
          "w-[100vw] md:max-w-[90vw] lg:max-w-[60vw]",
          "flex flex-col overflow-y-auto p-0"
        )}
      >
        <SheetHeader className="px-6 pt-6">
          <div className="flex flex-col items-start gap-1">
            <SheetTitle className="text-2xl font-bold">
              Dispatch Crew
            </SheetTitle>
            <SheetDescription>
              <div className="text-gray-800 flex flex-row items-center gap-1 ">
                <span>
                  <MessageSquareDot className="w-3 h-3" />
                </span>{" "}
                Send the crew a text notifying them of this job.
              </div>
            </SheetDescription>
          </div>
        </SheetHeader>
        <div className="flex flex-col gap-6 px-6 mb-24 overflow-y-auto">
          <div className="flex flex-col gap-2 divide-y divide-slate-200">
            <div className="flex flex-row gap-4 items-center py-1">
              <div className="w-1/4 flex flex-row gap-1 items-center text-slate-700 text-sm">
                {/* <FontAwesomeIcon icon={faPerson} className="w-4 h-4" /> */}
                Customer
              </div>
              <div className="text-sm font-medium">{move.customer_name}</div>
            </div>

            <div className="flex flex-row gap-4 items-center py-1">
              <div className="w-1/4 flex flex-row gap-1 items-center text-slate-700 text-sm">
                {/* <FontAwesomeIcon icon={faPerson} className="w-4 h-4" /> */}
                Date &amp; Arrival Window
              </div>
              <div className="text-sm font-medium space-x-1">
                <span>
                  {formatDateWithOrdinal(
                    parseISO(move.service_date.toString()),
                    {
                      showDayOfWeek: true,
                      showYear: false,
                    }
                  )}{" "}
                  /
                </span>
                <span>
                  {formatStartWindow(
                    move.start_window_begin_hour,
                    move.start_window_begin_minute,
                    move.start_window_begin_meridiem
                  )}{" "}
                  to{" "}
                  {formatStartWindow(
                    move.start_window_end_hour,
                    move.start_window_end_minute,
                    move.start_window_end_meridiem
                  )}{" "}
                  /
                </span>
                <span>
                  {Number(move.hours_estimate)}-
                  {Number(move.hours_estimate) + 1} hour job
                </span>
              </div>
            </div>

            <div className="flex flex-row gap-4 items-center py-1">
              <div className="w-1/4 flex flex-row gap-1 items-center text-slate-700 text-sm">
                Job Details
              </div>
              <div className="text-sm font-medium">
                {SERVICE_TYPES[move.service as keyof typeof SERVICE_TYPES] ||
                  move.service}{" "}
                {move.kind && formatMoveKind(move)}{" "}
                {move.size && `(${move.size})`}
                {move.area && `(${move.area} sq.ft)`}
              </div>
            </div>

            <div className="flex flex-row gap-4 items-center py-1">
              <div className="w-1/4 flex flex-row gap-1 items-center text-slate-700 text-sm">
                Locations
              </div>
              <div className="text-sm font-medium">
                <div className="flex flex-row items-center gap-2">
                  <div>{move.origin.city}</div>
                  {move.stops?.map((stop: Stop) => (
                    <div className="flex flex-row items-center gap-1">
                      <div>&rarr;</div>
                      <div>{stop.city}</div>
                    </div>
                  ))}
                  {move.destination?.street && (
                    <div className="flex flex-row items-center gap-1">
                      <div>&rarr;</div>
                      <div>{move.destination.city}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-row gap-4 items-center py-1">
              <div className="w-1/4 flex flex-row gap-1 items-center text-slate-700 text-sm">
                Movers &amp; Trucks
              </div>
              <div className="text-sm font-medium">
                {move.num_of_movers} movers, {move.num_of_trucks} trucks
              </div>
            </div>
          </div>

          <div>
            {mover?.crew.length === 0 ? (
              <div className="text-base font-semibold">
                You have not setup your crew yet.
                <br />
                Please add your crew members under Settings.
              </div>
            ) : (
              <div key={ts}>
                <h4 className="text-sm font-semibold mb-2">
                  Movers{" "}
                  <span className="text-gray-700">
                    ({assignedCrew.length}/{move.num_of_movers} assigned)
                  </span>
                </h4>
                <div className="flex flex-col gap-0.5 text-sm">
                  {allCrew.map((crew) => (
                    <div
                      key={crew.id}
                      className={cn(
                        "flex flex-row items-center gap-1 grid grid-cols-9 border-b border-1  border-stone-100 border-solid py-1.5 py-2"
                      )}
                    >
                      <div className="col-span-3 flex flex-row items-start gap-2">
                        {/* {isCrewMember(crew) && (
                          <CheckSquare className="w-4 h-4 text-green-700 text-right" />
                        )} */}
                        <span className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center">
                          <FontAwesomeIcon
                            icon={faUserHair}
                            className="w-3 h-3"
                          />
                        </span>
                        <div className="space-y-0.5 flex flex-col">
                          <span className="text-sm font-semibold">
                            {crew.first_name} {crew.last_name}
                          </span>
                          <span className="text-xs font-normal">
                            {movingCrew.find((mc) => mc.user_id === crew.id) &&
                              `${
                                CREW_ROLES[
                                  movingCrew.find(
                                    (mc) => mc.user_id === crew.id
                                  )?.role as keyof typeof CREW_ROLES
                                ]
                              }`}
                          </span>
                        </div>
                      </div>
                      {isCrewMember(crew) && renderCrewMemberActions(crew)}
                      {isAvailableCrew(crew) &&
                        renderAvailableCrewActions(crew)}
                      {isNotAvailableCrew(crew) &&
                        renderNotAvailableCrewActions(crew)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <SheetFooter className="w-full bg-white absolute bottom-0 left-0">
          <div className="w-full p-4 border-t border-slate-200">
            <div className="flex flex-row items-center gap-4">
              <Button
                onClick={save}
                disabled={isUpdating}
                size="lg"
                className="flex flex-row items-center gap-1.5"
              >
                <FontAwesomeIcon icon={faPaperPlane} className="w-3 h-3" />
                {isUpdating ? "Dispatching..." : "Save and Dispatch"}
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
            </div>
          </div>

          <div
            className={cn(
              "hidden fixed bottom-0 -ml-6 px-4 py-2 bg-white border-t border-stone-300 border-solid bg-gray-100 text-white z-10",
              "w-[90vw] max-w-[90vw]",
              "sm:max-w-[90vw]",
              "lg:max-w-[60vw]"
            )}
          >
            <div className="text-sm text-black flex flex-row items-start gap-1">
              {/* <InfoCircledIcon className="w-4 h-4" /> */}
              <div>
                <span>
                  Crew only sees jobs they're assigned to. <br />
                  <span className="font-semibold">Leaders</span> see rates and
                  can process billing.{" "}
                  <span className="font-semibold">Helpers</span> see limited
                  information (no billing or rates).
                </span>
              </div>
            </div>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
